// src/ui/CustomInstrumentDialog.js
// Modal dialog for creating custom sampler instruments from imported audio files.
// Opens via showOpenFilePicker, parses filenames for note names,
// stores samples in IndexedDB, and registers the instrument.

import { SampleStorage } from '../project/SampleStorage.js';

export class CustomInstrumentDialog {
  /**
   * @param {SampleStorage} sampleStorage
   * @param {import('../instruments/CustomInstrumentRegistry.js').CustomInstrumentRegistry} registry
   * @param {function} onCreated - called with the registered instrument entry when creation completes
   */
  constructor(sampleStorage, registry, onCreated) {
    this.sampleStorage = sampleStorage;
    this.registry = registry;
    this.onCreated = onCreated;
    this._samples = []; // [{ file, name, note, ref, status: 'pending'|'stored'|'error' }]
  }

  /** Open the modal dialog. */
  open() {
    if (this._overlay) return; // already open

    this._samples = [];
    this._overlay = document.createElement('div');
    this._overlay.className = 'modal-overlay';
    this._overlay.innerHTML = `
      <div class="modal-content custom-instrument-modal">
        <h3>Create Custom Instrument</h3>
        <div class="ci-field">
          <label>Instrument name</label>
          <input type="text" id="ci-name" class="ci-input" placeholder="e.g. My Piano" value="" />
        </div>
        <div class="ci-field">
          <label>Release</label>
          <input type="range" id="ci-release" min="0.1" max="10" step="0.1" value="1.2" />
          <span id="ci-release-label">1.2s</span>
        </div>
        <div class="ci-field">
          <label>Color</label>
          <input type="color" id="ci-color" value="#7cb342" />
        </div>
        <div class="ci-field">
          <button id="ci-add-samples" class="ci-btn">+ Add Samples</button>
        </div>
        <div id="ci-sample-list" class="ci-sample-list">
          <p class="ci-empty">No samples added yet. Click "Add Samples" to import audio files.</p>
        </div>
        <div class="ci-actions">
          <button id="ci-cancel" class="ci-btn ci-btn-secondary">Cancel</button>
          <button id="ci-create" class="ci-btn ci-btn-primary" disabled>Create Instrument</button>
        </div>
      </div>
    `;

    document.body.appendChild(this._overlay);

    // Wire events
    this._overlay.querySelector('#ci-cancel').addEventListener('click', () => this.close());
    this._overlay.querySelector('#ci-name').addEventListener('input', () => this._validate());
    this._overlay.querySelector('#ci-release').addEventListener('input', (e) => {
      this._overlay.querySelector('#ci-release-label').textContent = `${parseFloat(e.target.value).toFixed(1)}s`;
    });
    this._overlay.querySelector('#ci-add-samples').addEventListener('click', () => this._importSamples());
    this._overlay.querySelector('#ci-create').addEventListener('click', () => this._createInstrument());

    // Close on overlay click (not on content click)
    this._overlay.addEventListener('click', (e) => {
      if (e.target === this._overlay) this.close();
    });
  }

  close() {
    if (this._overlay) {
      this._overlay.remove();
      this._overlay = null;
    }
  }

  async _importSamples() {
    if (!window.showOpenFilePicker) { alert('File import requires Chrome or Edge.'); return; }
    try {
      const handles = await window.showOpenFilePicker({
        multiple: true,
        types: [{
          description: 'Audio Files',
          accept: { 'audio/*': ['.wav', '.ogg', '.mp3'] }
        }]
      });

      for (const handle of handles) {
        const file = await handle.getFile();
        const note = this._parseNoteFromFilename(file.name);

        this._samples.push({
          file,
          name: file.name,
          note: note || '',
          ref: this.sampleStorage.generateRef(),
          status: 'pending',
          handle
        });
      }

      this._renderSampleList();
      this._validate();
    } catch (err) {
      if (err.name !== 'AbortError') console.error('Import error:', err);
    }
  }

  /** Parse a note name from a filename like "piano-C4.wav" or "F#3.ogg". */
  _parseNoteFromFilename(filename) {
    const match = filename.match(/([A-G][#b]?\d+)/);
    return match ? match[1] : '';
  }

  _renderSampleList() {
    const list = this._overlay?.querySelector('#ci-sample-list');
    if (!list) return;
    if (this._samples.length === 0) {
      list.innerHTML = '<p class="ci-empty">No samples added yet.</p>';
      return;
    }
    let html = '<table class="ci-sample-table"><tr><th>File</th><th>Note</th><th>Status</th></tr>';
    for (const s of this._samples) {
      const noteVal = s.note || '';
      html += `<tr>
        <td>${s.name.replace(/</g, '&lt;')}</td>
        <td><input type="text" class="ci-note-input" value="${noteVal}" data-ref="${s.ref}" placeholder="e.g. C4" /></td>
        <td class="ci-status-${s.status}">${s.status === 'stored' ? '✓' : s.status === 'error' ? '✗' : 'pending'}</td>
      </tr>`;
    }
    html += '</table>';
    list.innerHTML = html;

    // Wire note input changes
    list.querySelectorAll('.ci-note-input').forEach(input => {
      input.addEventListener('change', (e) => {
        const ref = e.target.dataset.ref;
        const s = this._samples.find(x => x.ref === ref);
        if (s) s.note = e.target.value;
        this._validate();
      });
    });
  }

  _validate() {
    const name = this._overlay?.querySelector('#ci-name')?.value?.trim();
    const hasMappedSamples = this._samples.some(s => s.note && s.status !== 'error');
    const btn = this._overlay?.querySelector('#ci-create');
    if (btn) btn.disabled = !(name && hasMappedSamples);
  }

  async _createInstrument() {
    const name = this._overlay.querySelector('#ci-name').value.trim();
    const release = parseFloat(this._overlay.querySelector('#ci-release').value);
    const color = this._overlay.querySelector('#ci-color').value;

    const createBtn = this._overlay.querySelector('#ci-create');
    createBtn.disabled = true;
    createBtn.textContent = 'Storing samples…';

    // Store all samples in IndexedDB
    const sampleMap = {};
    for (const s of this._samples) {
      if (!s.note) continue;
      try {
        const arrayBuffer = await s.file.arrayBuffer();
        await this.sampleStorage.storeSample(s.ref, arrayBuffer, s.name);
        sampleMap[s.note] = s.ref;
        s.status = 'stored';
      } catch (err) {
        console.error(`Failed to store sample ${s.name}:`, err);
        s.status = 'error';
      }
    }

    this._renderSampleList();

    const noteCount = Object.keys(sampleMap).length;
    if (noteCount === 0) {
      createBtn.disabled = false;
      createBtn.textContent = 'Create Instrument';
      alert('No samples were stored successfully. Check file formats.');
      return;
    }

    // Register the custom instrument
    const id = this.registry.register({
      name,
      sampleMap,
      release,
      color
    });

    createBtn.textContent = 'Created!';
    setTimeout(() => {
      this.close();
      if (this.onCreated) {
        this.onCreated({ id, name, sampleMap, release, color, type: 'custom' });
      }
    }, 500);
  }
}
